// API base URL — picks dev server only for actual local-browser dev.
//
// Tricky bit: a Capacitor APK loads the React bundle from `https://localhost/`
// inside the WebView, so `window.location.hostname === 'localhost'` is TRUE
// in production on a phone. If we used that as the dev-mode signal we'd
// route every API call to `http://localhost:5000` on the phone — which
// doesn't exist, hence "Network Error: Failed to fetch" on every login.
//
// Detection layered most-specific → least-specific:
//   1. window.Capacitor.isNativePlatform()  → APK / iOS bundle. Always
//      use the Render backend, regardless of hostname.
//   2. window.location.protocol === 'http:' + hostname === 'localhost'
//      → real `npm start` dev server. Use the local Express backend.
//   3. Anything else (lacto.in, render web, https://localhost weirdness)
//      → Render backend.
// Branded backend: api.prorentz.com is a custom domain on the Render service.
// Overridable via REACT_APP_API_URL at build time so the URL is never hard-pinned.
// The old rajieev-house-rental.onrender.com still resolves (kept alive for APKs
// built before this switch), so nothing breaks during the transition.
const PROD_API = process.env.REACT_APP_API_URL || 'https://api.prorentz.com/api';
const DEV_API  = 'http://localhost:5000/api';
const _isCapacitor = typeof window !== 'undefined'
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform();
const _isDevBrowser = typeof window !== 'undefined'
    && window.location.protocol === 'http:'
    && window.location.hostname === 'localhost';
export const API_URL = _isCapacitor ? PROD_API : (_isDevBrowser ? DEV_API : PROD_API);

// JWT Token helper — reads from localStorage
const getToken = () => localStorage.getItem('auth_token') || null;
const getAuthHeaders = (extra = {}) => {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json', ...extra };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
};

// Force logout helper — clears all auth state (NO reload to avoid loops)
const forceLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('loggedIn');
    localStorage.removeItem('role');
    localStorage.removeItem('currUserId');
    localStorage.removeItem('formerTenant');
    // Per-account cache — a different user logging in on this device must
    // not inherit the previous landlord's maintenance assignee/expense data.
    localStorage.removeItem('maint_assignees');
    // Tell the React layer the session died — this module can't touch
    // React state, and without the event a mid-session 401 left the UI
    // fully rendered with a dead token (every save failing) until a
    // manual refresh. App.js listens and routes to the login screen.
    try { window.dispatchEvent(new Event('auth:expired')); } catch { /* SSR/test */ }
};

// Safe fetch wrapper with retry logic for Render cold starts + auto auth headers
const safeFetch = async (url, options = {}, retries = 2) => {
    // Auto-inject auth headers if not already present
    if (!options.headers || !options.headers['Authorization']) {
        options.headers = { ...options.headers, 'Authorization': `Bearer ${getToken()}` };
    }
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const res = await fetch(url, options);
            // If 401, token expired — clear auth state silently
            if (res.status === 401) {
                forceLogout();
                return null;
            }
            return res;
        } catch (e) {
            console.error(`API call failed (attempt ${attempt + 1}): ${url}`, e);
            if (attempt < retries) {
                await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); // Exponential backoff
            }
        }
    }
    return null;
};

// FIX: Safe JSON parse from response
const safeJson = async (res, fallback = null) => {
    if (!res) return fallback;
    try {
        return await res.json();
    } catch (e) {
        console.error("Failed to parse JSON response", e);
        return fallback;
    }
};

export const api = {
    // Auth — login does NOT need auth token (public endpoint)
    login: async (role, ph, password) => {
        try {
            const res = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, ph, password })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
                // Read-only "former tenant" session marker (set by the server
                // when ALL of this tenant's tenancies have been settled). TenDash
                // reads this to render the history-only view instead of force-
                // logging the user out. Cleared on any normal/active login and
                // on logout so the flag never lingers for an active tenant.
                if (data.formerTenant) localStorage.setItem('formerTenant', 'true');
                else localStorage.removeItem('formerTenant');
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    selectOrg: async (tenantId, ph, password) => {
        try {
            const res = await fetch(`${API_URL}/login/select-org`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantId, ph, password })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Get all orgs for current tenant
    getMyOrgs: async () => {
        const res = await safeFetch(`${API_URL}/my-orgs`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, orgs: [] });
    },

    // Switch org without re-login (authenticated)
    switchOrg: async (targetOrgId) => {
        try {
            const res = await fetch(`${API_URL}/switch-org`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ targetOrgId })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
                if (data.user?.id) localStorage.setItem('currUserId', data.user.id);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Switch to one of the caller's PAST (settled-out) tenancies — read-only
    // former-tenant portal. Keeps the formerTenant marker set.
    formerSwitch: async (tenantId) => {
        try {
            const res = await fetch(`${API_URL}/former/switch`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ tenantId })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
                if (data.user?.id) localStorage.setItem('currUserId', data.user.id);
                localStorage.setItem('formerTenant', 'true');
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // ── OTP-based password reset (replaces the legacy single /forgot-password) ──
    // Step 1: send a 6-digit reset code. `identifier` is an email OR a phone —
    // email → code goes to that email; phone → SMS (when configured) else the
    // account's on-file email. Always responds 200 to prevent account enumeration.
    requestPasswordResetOtp: async (role, identifier) => {
        try {
            const res = await fetch(`${API_URL}/forgot-password/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, identifier })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Step 2: submit OTP + new password.
    verifyPasswordResetOtp: async (role, identifier, otp, newPassword) => {
        try {
            const res = await fetch(`${API_URL}/forgot-password/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, identifier, otp, newPassword })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Legacy single-call reset — kept as a thin shim that returns the 410
    // server response so any old caller surfaces a clear error.
    forgotPassword: async () => {
        return { success: false, message: 'Reset method updated. Please use the new OTP flow.' };
    },
    // Change password for the currently logged-in user. Requires JWT + current password.
    // Intentionally does NOT use safeFetch because a 401 here means "current password wrong",
    // not "token expired" — we don't want to force-logout on that.
    changePassword: async (currentPassword, newPassword) => {
        try {
            const res = await fetch(`${API_URL}/change-password`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ currentPassword, newPassword })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },
    signup: async (role, ph, password, name, orgName, skill, termsVersion, email) => {
        try {
            const res = await fetch(`${API_URL}/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, ph, password, name, orgName, skill, termsVersion, email })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Google Sign-In — verify credential with backend, get JWT
    googleLogin: async (credential, signupRole, phone, orgName, skill, displayName, termsVersion) => {
        try {
            const body = { credential };
            if (signupRole) body.signupRole = signupRole;
            if (phone) body.phone = phone;
            if (orgName) body.orgName = orgName;
            if (skill) body.skill = skill;
            if (displayName) body.displayName = displayName;
            if (termsVersion) body.termsVersion = termsVersion;
            const res = await fetch(`${API_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Logout — clear token
    logout: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('role');
        localStorage.removeItem('currUserId');
        localStorage.removeItem('googleUser');
        localStorage.removeItem('formerTenant');
    },

    // Sync Data
    sync: async () => {
        try {
            const res = await fetch(`${API_URL}/sync`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) {
                if (res.status === 401) {
                    forceLogout();
                    return null;
                }
                console.error("Sync HTTP error:", res.status);
                return null;
            }
            let data = await res.json();

            // Recover cached maintenance data from LocalStorage as backup
            const cachedMts = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
            if (data && data.maints) {
                data.maints = data.maints.map(m => {
                    if (cachedMts[m.id]) {
                        // Only use cache when the server doesn't return the
                        // field AT ALL (?? not ||). With ||, a value the
                        // landlord cleared server-side ("", 0, false) was
                        // resurrected from this device's cache forever —
                        // worst case a stale exp_applied=true blocked a
                        // legitimate expense from ever being charged.
                        return {
                            ...m,
                            asn_n: m.asn_n ?? cachedMts[m.id].asn_n,
                            asn_p: m.asn_p ?? cachedMts[m.id].asn_p,
                            asn_notes: m.asn_notes ?? cachedMts[m.id].asn_notes,
                            exp_amt: m.exp_amt ?? cachedMts[m.id].exp_amt ?? 0,
                            exp_target: m.exp_target ?? cachedMts[m.id].exp_target ?? 'all',
                            exp_applied: m.exp_applied ?? cachedMts[m.id].exp_applied ?? false
                        };
                    }
                    return m;
                });
            }
            return data;
        } catch (e) {
            console.error("Sync failed:", e);
            return null;
        }
    },

    // Properties
    saveProperty: async (prop) => {
        const res = await safeFetch(`${API_URL}/properties`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(prop)
        });
        return safeJson(res, { success: false });
    },

    deleteProperty: async (id) => {
        const res = await safeFetch(`${API_URL}/properties/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Tenants
    updateProfile: async (data) => {
        const res = await safeFetch(`${API_URL}/profile`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    // Update the current user's account email (all roles) — canonical column for OTP.
    updateEmail: async (email) => {
        const res = await safeFetch(`${API_URL}/account/email`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ email })
        });
        return safeJson(res, { success: false });
    },

    saveTenant: async (ten) => {
        const res = await safeFetch(`${API_URL}/tenants`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(ten)
        });
        return safeJson(res, { success: false });
    },

    // Tenant-self e-signature save. POST /tenants is landlord-only, so
    // when a tenant signed their lease we couldn't actually persist the
    // image — the React state showed "Signed" but the row never updated
    // and the landlord saw an empty signature on their next sync. This
    // hits the dedicated tenant-self endpoint instead.
    saveTenantEsign: async (esign) => {
        const res = await safeFetch(`${API_URL}/tenants/me/esign`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ esign }),
        });
        return safeJson(res, { success: false });
    },

    // Tenant Association Requests (consent-based linking)
    lookupTenant: async (phone) => {
        const res = await safeFetch(`${API_URL}/tenants/lookup/${encodeURIComponent(phone)}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },
    sendTenantRequest: async (data) => {
        const res = await safeFetch(`${API_URL}/tenants/request`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    getMyRequests: async () => {
        const res = await safeFetch(`${API_URL}/tenants/my-requests`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, requests: [] });
    },
    respondToRequest: async (reqId, action) => {
        const res = await safeFetch(`${API_URL}/tenants/request/${reqId}/respond`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ action }) });
        return safeJson(res, { success: false });
    },
    getLandlordRequests: async () => {
        const res = await safeFetch(`${API_URL}/tenants/requests`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, requests: [] });
    },
    convertLead: async (leadId) => {
        const res = await safeFetch(`${API_URL}/tenants/from-lead`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ leadId }) });
        return safeJson(res, { success: false });
    },

    deleteTenant: async (id) => {
        const res = await safeFetch(`${API_URL}/tenants/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Payments
    savePayment: async (pay) => {
        const res = await safeFetch(`${API_URL}/payments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(pay)
        });
        return safeJson(res, { success: false });
    },

    deletePayment: async (id) => {
        const res = await safeFetch(`${API_URL}/payments/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Messages
    sendMessage: async (msg) => {
        const res = await safeFetch(`${API_URL}/messages`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(msg)
        });
        return safeJson(res, { success: false });
    },

    // Notifications
    saveNotification: async (notif) => {
        try {
            await fetch(`${API_URL}/notifications`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(notif)
            });
        } catch (e) { console.error("Notif save failed:", e); }
    },

    markRead: async (role, tid) => {
        const res = await safeFetch(`${API_URL}/notifications/read`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ role, tid })
        });
        return safeJson(res, { success: false });
    },

    // --- Web Push ---
    // Public — VAPID public key (no auth needed). Cached at the call site.
    getVapidKey: async () => {
        try {
            const res = await fetch(`${API_URL}/push/vapid-key`);
            return await res.json();
        } catch (e) {
            console.error("VAPID key fetch failed:", e);
            return { key: '' };
        }
    },
    // Register or refresh this device's push subscription. `sub` is the
    // shape you get from PushSubscription.toJSON() in the browser.
    subscribePush: async (sub) => {
        const res = await safeFetch(`${API_URL}/push/subscribe`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(sub),
        });
        return safeJson(res, { success: false });
    },
    // Remove a device subscription by endpoint URL.
    unsubscribePush: async (endpoint) => {
        const res = await safeFetch(`${API_URL}/push/unsubscribe`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ endpoint }),
        });
        return safeJson(res, { success: false });
    },

    // Maintenance
    saveMaintenance: async (maint) => {
        // Cache maintenance data locally as backup. Always write the
        // CURRENT values (including cleared ones) — the old truthy-only
        // gate meant a clear never reached the cache, so the stale value
        // came back on every sync.
        try {
            const cache = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
            cache[maint.id] = { asn_n: maint.asn_n, asn_p: maint.asn_p, asn_notes: maint.asn_notes, exp_amt: maint.exp_amt || 0, exp_target: maint.exp_target || 'all', exp_applied: maint.exp_applied || false };
            localStorage.setItem('maint_assignees', JSON.stringify(cache));
        } catch (e) { console.error("Cache write failed:", e); }

        const res = await safeFetch(`${API_URL}/maintenance`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(maint)
        });
        return safeJson(res, { success: false });
    },

    deleteMaintenance: async (id) => {
        // Also clean up local cache
        try {
            const cache = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
            delete cache[id];
            localStorage.setItem('maint_assignees', JSON.stringify(cache));
        } catch (e) { /* ignore */ }

        const res = await safeFetch(`${API_URL}/maintenance/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Common Charges
    saveCC: async (cc) => {
        const res = await safeFetch(`${API_URL}/common_charges`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(cc)
        });
        return safeJson(res, { success: false });
    },

    deleteCC: async (id) => {
        const res = await safeFetch(`${API_URL}/common_charges/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Vacate
    requestVacate: async (tid) => {
        const res = await safeFetch(`${API_URL}/vacate/request`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid })
        });
        return safeJson(res, { success: false });
    },

    approveVacate: async (tid, pid) => {
        const res = await safeFetch(`${API_URL}/vacate/approve`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid, pid })
        });
        return safeJson(res, { success: false });
    },

    rejectVacate: async (tid) => {
        const res = await safeFetch(`${API_URL}/vacate/reject`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid })
        });
        return safeJson(res, { success: false });
    },

    // Phase 17 vacate flow — new state machine. Coexists with the legacy
    // requestVacate / approveVacate / rejectVacate above so old UI keeps
    // working while the new VacatePage rolls out.
    vacateInitiate: async ({ lease_id, tenant_id, tenant_notes }) => {
        const res = await safeFetch(`${API_URL}/vacate/initiate`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ lease_id, tenant_id, tenant_notes }),
        });
        return safeJson(res, { success: false });
    },
    vacateListMine: async () => {
        const res = await safeFetch(`${API_URL}/vacate/mine`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, vacate_requests: [] });
    },
    vacateListOrg: async () => {
        const res = await safeFetch(`${API_URL}/vacate/org`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, vacate_requests: [] });
    },
    vacateGet: async (id) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },
    // Lookup the deposit settlement for a given lease — used by
    // AgreementPage to surface a "View Settlement Receipt" button on
    // terminated leases. Returns { success, settlement, vacate_request,
    // photos, deductions } on hit or { success: false, code: 'no_settlement' }
    // when the lease has no settlement on file (e.g. lease terminated
    // outside the vacate flow, or vacate was cancelled).
    vacateSettlementByLease: async (leaseId) => {
        const res = await safeFetch(`${API_URL}/vacate/settlement-by-lease/${leaseId}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },
    // Backfill the four "Tenant Agreement Details" fields onto the
    // tenant's profile. Called from AgreementPage saveEdit so the
    // landlord's typed values get remembered for future leases / renewals.
    // Server is "fill empty only" — never overwrites existing tenant data.
    updateTenantAgreementFields: async (id, fields) => {
        const res = await safeFetch(`${API_URL}/tenants/${id}/agreement-fields`, {
            method: 'PATCH', headers: getAuthHeaders(),
            body: JSON.stringify(fields),
        });
        return safeJson(res, { success: false });
    },
    vacateAddPhoto: async (id, { category, url, caption, notes }) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/photos`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ category, url, caption, notes }),
        });
        return safeJson(res, { success: false });
    },
    vacateDeletePhoto: async (id, pid) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/photos/${pid}`, {
            method: 'DELETE', headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },
    vacateUpdateNotes: async (id, { tenant_notes, landlord_notes }) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/notes`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ tenant_notes, landlord_notes }),
        });
        return safeJson(res, { success: false });
    },
    // State transitions. Server validates allowed roles + current state.
    // Optional body lets callers attach a tenant/landlord signature
    // (accept, settle) or a dispute reason. Server-side validation
    // ignores unknown / malformed payloads.
    vacateTransition: async (id, action, body = null) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/${action}`, {
            method: 'POST', headers: getAuthHeaders(),
            body: body ? JSON.stringify(body) : undefined,
        });
        return safeJson(res, { success: false });
    },

    // Phase 6 — admin dispute queue.
    adminVacateListDisputed: async () => {
        const res = await safeFetch(`${API_URL}/admin/vacate/disputed`, {
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false, disputed: [] });
    },

    // Deductions (Phase 3 — landlord composer)
    vacateAddDeduction: async (id, { reason, amount_paise, description, proof_photo_ids }) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/deductions`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ reason, amount_paise, description, proof_photo_ids }),
        });
        return safeJson(res, { success: false });
    },
    vacateDeleteDeduction: async (id, did) => {
        const res = await safeFetch(`${API_URL}/vacate/${id}/deductions/${did}`, {
            method: 'DELETE', headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },
    // Reduce / edit an existing deduction without deleting it — used
    // when a landlord wants to lower a disputed charge instead of
    // striking it entirely. All body fields are optional; only the
    // ones provided are updated server-side.
    vacateUpdateDeduction: async (id, did, { amount_paise, description, reason }) => {
        const body = {};
        if (typeof amount_paise === 'number') body.amount_paise = amount_paise;
        if (typeof description === 'string') body.description = description;
        if (typeof reason === 'string') body.reason = reason;
        const res = await safeFetch(`${API_URL}/vacate/${id}/deductions/${did}`, {
            method: 'PATCH', headers: getAuthHeaders(),
            body: JSON.stringify(body),
        });
        return safeJson(res, { success: false });
    },

    // Settings (with PER-USER localStorage cache so UPI etc. persist when
    // Render is cold-starting). The cache key MUST be scoped to the
    // currently-logged-in user — otherwise the previous user's settings
    // get served to whoever logs in next on the same browser. That
    // exact bug surfaced as: tenant unrelated to landlord A still seeing
    // landlord A's UPI + bank details after a Render hiccup, because the
    // cache from a prior landlord-A session was being read back.
    getSettings: async () => {
        const userId = localStorage.getItem('currUserId') || 'anon';
        const cacheKey = `cached_settings_${userId}`;
        try {
            const token = getToken();
            // No token = nobody is logged in. Don't return any cached
            // settings — there's no caller to scope them to.
            if (!token) return {};
            const res = await fetch(`${API_URL}/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Settings fetch failed');
            const data = await res.json();
            if (data && Object.keys(data).length > 0) {
                localStorage.setItem(cacheKey, JSON.stringify(data));
            } else {
                // Server returned {} — clear any stale cache for this user
                // so we don't accidentally serve it later.
                localStorage.removeItem(cacheKey);
            }
            return data;
        } catch (e) {
            console.error("Failed to fetch settings, using cache:", e);
            // Only the CURRENT user's cache is eligible — never another user's.
            try {
                const cached = JSON.parse(localStorage.getItem(cacheKey) || '{}');
                return cached;
            } catch { return {}; }
        }
    },

    saveSetting: async (key, value) => {
        const res = await safeFetch(`${API_URL}/settings`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ key, value })
        });
        return safeJson(res, { success: false });
    },

    // Service Person sync
    serviceSync: async (spId) => {
        try {
            const res = await fetch(`${API_URL}/service/sync/${spId}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) { console.error("Service sync failed:", e); return null; }
    },

    serviceUpdateJob: async (data) => {
        const res = await safeFetch(`${API_URL}/service/update-job`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    serviceVerifyCompletion: async (token, tid) => {
        const res = await safeFetch(`${API_URL}/service/verify-completion`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ token, tid })
        });
        return safeJson(res, { success: false });
    },

    saveServicePerson: async (sp) => {
        const res = await safeFetch(`${API_URL}/service_persons`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(sp)
        });
        return safeJson(res, { success: false });
    },

    deleteServicePerson: async (id) => {
        const res = await safeFetch(`${API_URL}/service_persons/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    saveServicePayment: async (pay) => {
        const res = await safeFetch(`${API_URL}/service_payments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(pay)
        });
        return safeJson(res, { success: false });
    },

    // Leases
    getLeases: async () => {
        const res = await safeFetch(`${API_URL}/leases`, { headers: getAuthHeaders() });
        return safeJson(res, []);
    },
    saveLease: async (lease) => {
        const res = await safeFetch(`${API_URL}/leases`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(lease)
        });
        return safeJson(res, { success: false });
    },

    deleteLease: async (id) => {
        const res = await safeFetch(`${API_URL}/leases/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Admin
    adminGetOrgs: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/orgs${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, orgs: [] });
    },

    adminGetOrg: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/orgs/${id}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, org: null });
    },

    adminPatchOrg: async (id, data) => {
        const res = await safeFetch(`${API_URL}/admin/orgs/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminGetStats: async () => {
        const res = await safeFetch(`${API_URL}/admin/stats`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, stats: {} });
    },

    adminGetRevenue: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/stats/revenue${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, paid: 0, pending: 0, pendingCount: 0, failed: 0, failedCount: 0, label: '' });
    },
    // Drill-down: the orders behind a revenue bucket (paid|pending|failed).
    adminGetRevenueList: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/stats/revenue/list${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, items: [] });
    },

    adminGetTrends: async (months = 6) => {
        const res = await safeFetch(`${API_URL}/admin/stats/trends?months=${months}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, months: [], planSplit: [] });
    },

    adminGetProperties: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/properties${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, properties: [] });
    },

    adminGetTenants: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/tenants${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, tenants: [] });
    },

    adminGetServicePersons: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/service-persons${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, servicePersons: [] });
    },

    adminGetPricing: async () => {
        const res = await safeFetch(`${API_URL}/admin/pricing`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, config: {} });
    },

    adminUpdatePricing: async (data) => {
        const res = await safeFetch(`${API_URL}/admin/pricing`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminGetAuditLogs: async () => {
        const res = await safeFetch(`${API_URL}/admin/audit-logs`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, logs: [] });
    },

    // Admin Reports
    adminReport: async (type, params = {}) => {
        const qs = new URLSearchParams();
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        if (params.plan && params.plan !== 'all') qs.set('plan', params.plan);
        const url = `${API_URL}/admin/reports/${type}` + (qs.toString() ? `?${qs}` : '');
        const res = await safeFetch(url, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, data: [] });
    },

    // Plans (Dynamic Pricing Engine)
    getPlans: async () => {
        const res = await safeFetch(`${API_URL}/plans`);
        return safeJson(res, { success: false, plans: [] });
    },

    adminGetPlans: async () => {
        const res = await safeFetch(`${API_URL}/admin/plans`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, plans: [] });
    },

    // Free-trial config (super-admin only): controls whether new landlord
    // signups get N days of a paid plan before being downgraded to Free.
    adminGetTrialConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/trial-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetTrialConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/trial-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    // Grace-period config (super-admin only): days a paid plan stays in
    // "expired but features still active" mode after plan_end before
    // the cron auto-downgrades it to Free.
    adminGetGraceConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/grace-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetGraceConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/grace-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    // Subscriptions master switch (super-admin only). When OFF, every
    // landlord effectively has full access — server-side plan guards,
    // property caps, feature gates, and the auto-downgrade cron all
    // short-circuit. Useful for migration / promo / kill-switch.
    adminGetSubscriptionsConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/subscriptions-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetSubscriptionsConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/subscriptions-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    // Feedback FAB master switch (super-admin only). When OFF, every
    // client hides the floating bug button. Existing reports in the
    // inbox stay accessible — this only gates submission UI.
    adminGetFeedbackButtonConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/feedback-button-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetFeedbackButtonConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/feedback-button-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    // Campaign broadcast (festival greeting + landlord discount).
    adminGetCampaignConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/campaign-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetCampaignConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/campaign-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    // Pricing mode switch (super-admin only). Mutually exclusive
    // toggle between 'fixed_tier' (Free/Starter/Growth/Pro) and
    // 'per_property'. Server derives the mode from state and
    // atomically flips one set of flags when the mode changes.
    adminGetPricingMode: async () => {
        const res = await safeFetch(`${API_URL}/admin/pricing-mode`, {
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false, mode: 'fixed_tier' });
    },
    adminSetPricingMode: async (mode) => {
        const res = await safeFetch(`${API_URL}/admin/pricing-mode`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ mode }),
        });
        return safeJson(res, { success: false });
    },

    // Per-property plan CRUD (super-admin only). Returns plans with a
    // server-computed `preview` table + a `cliff` warning if flat-mode
    // pricing makes (threshold + 1) cheaper than `threshold`.
    adminGetPerPropertyPlans: async () => {
        const res = await safeFetch(`${API_URL}/admin/per-property-plans`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, plans: [] });
    },
    adminUpdatePerPropertyPlan: async (id, data) => {
        const res = await safeFetch(`${API_URL}/admin/per-property-plans/${encodeURIComponent(id)}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        });
        return safeJson(res, { success: false });
    },

    // Bulk-migrate every paying org to a per-property plan. Preview is
    // a dry run (returns counts + sample org names); migrate-all is the
    // irreversible execute. Free orgs and orgs already on a per-prop
    // plan are skipped automatically server-side.
    adminPreviewMigrateAll: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/per-property-plans/${encodeURIComponent(id)}/migrate-preview`, {
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },
    adminMigrateAllToPerProperty: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/per-property-plans/${encodeURIComponent(id)}/migrate-all`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },

    adminCreatePlan: async (data) => {
        const res = await safeFetch(`${API_URL}/admin/plans`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminUpdatePlan: async (id, data) => {
        const res = await safeFetch(`${API_URL}/admin/plans/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminDeletePlan: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/plans/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false });
    },

    // Geocode status + backfill — admin only. Status is cheap (single
    // COUNT query); backfill iterates and calls Nominatim, so it can
    // take ~1 second per property without coords. Both return
    // { success, ... } — the backfill response includes a per-row
    // results array so the UI can show what was matched and what wasn't.
    adminGetGeocodeStatus: async () => {
        const res = await safeFetch(`${API_URL}/admin/geocode-status`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },
    adminGeocodeBackfill: async () => {
        const res = await safeFetch(`${API_URL}/admin/geocode-backfill`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },

    // Billing (Phase 14)
    createBillingOrder: async (planId, cycle) => {
        const res = await safeFetch(`${API_URL}/billing/create-order`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ plan_id: planId, cycle: cycle || 'monthly' })
        });
        return safeJson(res, { success: false });
    },

    verifyBillingPayment: async (paymentData) => {
        const res = await safeFetch(`${API_URL}/billing/verify-payment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        return safeJson(res, { success: false });
    },

    // Server-side reconciliation: ask Razorpay if the order has a captured
    // payment and activate the plan if so. Used as a fallback when the
    // inline Razorpay client handler doesn't fire (browser closed, JS
    // error, etc.).
    reconcileBillingOrder: async (orderId) => {
        const res = await safeFetch(`${API_URL}/billing/reconcile-order`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ razorpay_order_id: orderId })
        });
        return safeJson(res, { success: false });
    },

    getBillingHistory: async () => {
        const res = await safeFetch(`${API_URL}/billing/history`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, transactions: [] });
    },

    // ── Cashfree: landlord connects their OWN merchant account so rent
    // settles directly to them. Creds are verified + encrypted server-side.
    connectCashfree: async ({ client_id, client_secret, webhook_secret, env, business_name }) => {
        const res = await safeFetch(`${API_URL}/billing/cashfree/connect`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ client_id, client_secret, webhook_secret, env, business_name })
        });
        return safeJson(res, { success: false });
    },
    getCashfreeStatus: async () => {
        const res = await safeFetch(`${API_URL}/billing/cashfree/status`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, status: 'not_setup' });
    },
    disconnectCashfree: async () => {
        const res = await safeFetch(`${API_URL}/billing/cashfree/disconnect`, { method: 'POST', headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },

    // Razorpay Rent Payment (tenant pays rent online)
    createRentOrder: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/razorpay-order`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    verifyRentPayment: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/razorpay-verify`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    // ── Cashfree online rent (tenant → landlord's OWN account) ──
    // Whether the tenant's landlord accepts online rent right now.
    getOnlineRentAvailability: async () => {
        const res = await safeFetch(`${API_URL}/payments/online-availability`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, available: false });
    },
    // Create a Cashfree order for one due row. Returns payment_session_id
    // for the v3 SDK + cashfree_mode (sandbox|production).
    createCashfreeRentOrder: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/cashfree-order`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },
    // Create a Cashfree order to pay an ADVANCE online (no due row).
    createCashfreeAdvanceOrder: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/cashfree-advance-order`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },
    // Inline confirm after checkout (server re-checks with Cashfree).
    verifyCashfreeRent: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/cashfree-verify`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    // ── Reviews (two-sided landlord <-> tenant reputation) ──
    // Write a review (server re-checks eligibility from the tenancy).
    submitReview: async (data) => {
        const res = await safeFetch(`${API_URL}/reviews`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },
    // A landlord's public reputation (for tenants / marketplace).
    getLandlordReviews: async (orgId) => {
        const res = await safeFetch(`${API_URL}/reviews/landlord/${encodeURIComponent(orgId)}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, avg: 0, count: 0, reviews: [] });
    },
    // A tenant's reputation by phone (landlord-only; aggregate always,
    // written reviews only once this landlord has engaged the tenant).
    getTenantReputation: async (phone) => {
        const res = await safeFetch(`${API_URL}/reviews/tenant/${encodeURIComponent(phone)}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, avg: 0, count: 0, reviews: [], detailUnlocked: false });
    },
    // What can I review right now (mature tenancy, not yet reviewed)?
    getEligibleReviews: async () => {
        const res = await safeFetch(`${API_URL}/reviews/eligible`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, items: [] });
    },
    reportReview: async (id, reason) => {
        const res = await safeFetch(`${API_URL}/reviews/${encodeURIComponent(id)}/report`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ reason })
        });
        return safeJson(res, { success: false });
    },

    // Phase 16: Public property browsing (no auth)
    getPublicProperties: async (prefs = {}) => {
        try {
            const params = new URLSearchParams();
            if (prefs.rooms) params.set('rooms', prefs.rooms);
            if (prefs.max_rent) params.set('max_rent', prefs.max_rent);
            if (prefs.furnishing && prefs.furnishing !== 'any') params.set('furnishing', prefs.furnishing);
            if (prefs.property_type && prefs.property_type !== 'any') params.set('property_type', prefs.property_type);
            if (prefs.comm_type && prefs.comm_type !== 'any') params.set('comm_type', prefs.comm_type);
            const qs = params.toString();
            const res = await fetch(`${API_URL}/public/properties${qs ? '?' + qs : ''}`);
            if (!res.ok) return { success: false, properties: [] };
            return await res.json();
        } catch (e) {
            console.error("Public properties fetch failed:", e);
            return { success: false, properties: [] };
        }
    },

    // Phase 22: Get user's favorite property IDs
    getFavorites: async () => {
        const res = await safeFetch(`${API_URL}/user/favorites`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, favorites: [] });
    },

    // Phase 22: Toggle favorite (add/remove)
    toggleFavorite: async (propertyId) => {
        const res = await safeFetch(`${API_URL}/user/favorites/${propertyId}`, {
            method: 'POST', headers: getAuthHeaders()
        });
        return safeJson(res, { success: false });
    },

    // Phase 22: Get saved browse preferences
    getPreferences: async () => {
        const res = await safeFetch(`${API_URL}/user/preferences`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, preferences: {} });
    },

    // Phase 22: Save browse preferences
    savePreferences: async (prefs) => {
        const res = await safeFetch(`${API_URL}/user/preferences`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify(prefs)
        });
        return safeJson(res, { success: false });
    },

    // Phase 16: Submit lead inquiry (auth optional)
    submitLead: async (lead) => {
        try {
            // Send the session token when the caller is logged in (tenant /
            // landlord / service browsing in-app). The server treats a valid
            // token as a verified identity, so the in-app Send Inquiry form
            // skips the Google sign-in that anonymous public visitors need.
            const token = getToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const res = await fetch(`${API_URL}/leads`, {
                method: 'POST',
                headers,
                body: JSON.stringify(lead)
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: 'Network error: ' + e.message };
        }
    },

    // Phase 16: Get leads (landlord)
    getLeads: async () => {
        const res = await safeFetch(`${API_URL}/leads`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, leads: [] });
    },

    // The tenant's OWN sent inquiries (which properties they've messaged).
    getMyInquiries: async () => {
        const res = await safeFetch(`${API_URL}/my-inquiries`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, inquiries: [] });
    },

    // Remove inquiries from the tenant's own list (soft-hide; landlord keeps the lead).
    hideMyInquiries: async (ids) => {
        const res = await safeFetch(`${API_URL}/my-inquiries/hide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
            body: JSON.stringify({ ids })
        });
        return safeJson(res, { success: false });
    },

    // Retract inquiries entirely — hard-deletes the lead so the landlord no longer sees it.
    withdrawMyInquiries: async (ids) => {
        const res = await safeFetch(`${API_URL}/my-inquiries/withdraw`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
            body: JSON.stringify({ ids })
        });
        return safeJson(res, { success: false });
    },

    // Phase 16: Update lead status (landlord)
    updateLead: async (id, status) => {
        const res = await safeFetch(`${API_URL}/leads/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ status })
        });
        return safeJson(res, { success: false });
    },

    // Hard-delete a lead. The DB row goes away entirely (no FK
    // dependencies). Landlord-only and org-scoped on the server, so
    // callers can't reach other orgs' leads even with a guessed id.
    deleteLead: async (id) => {
        const res = await safeFetch(`${API_URL}/leads/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return safeJson(res, { success: false });
    },

    // Phase 18: Schedule visit for a lead
    scheduleLead: async (id, visit_date, visit_time) => {
        const res = await safeFetch(`${API_URL}/leads/${id}/schedule`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ visit_date, visit_time })
        });
        return safeJson(res, { success: false });
    },

    // Phase 18: Update visit status (completed/missed)
    updateVisitStatus: async (id, visit_status) => {
        const res = await safeFetch(`${API_URL}/leads/${id}/visit-status`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ visit_status })
        });
        return safeJson(res, { success: false });
    },

    // Auto-generate due payments
    generateDuePayments: async () => {
        try {
            const res = await fetch(`${API_URL}/payments/generate-due`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) return { success: false, generated: 0 };
            return await res.json();
        } catch (e) {
            console.error("Generate due payments failed:", e);
            return { success: false, generated: 0 };
        }
    },

    // Team management
    getTeam: async () => {
        const res = await safeFetch(`${API_URL}/team`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, members: [] });
    },
    addTeamMember: async (data) => {
        const res = await safeFetch(`${API_URL}/team`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    updateTeamMember: async (id, data) => {
        const res = await safeFetch(`${API_URL}/team/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    removeTeamMember: async (id) => {
        const res = await safeFetch(`${API_URL}/team/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },

    // Feedback / Bug Reports
    submitFeedback: async (data) => {
        const res = await safeFetch(`${API_URL}/feedback`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    getFeedback: async (status) => {
        const url = status ? `${API_URL}/admin/feedback?status=${status}` : `${API_URL}/admin/feedback`;
        const res = await safeFetch(url, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, feedback: [] });
    },
    updateFeedbackStatus: async (id, status) => {
        const res = await safeFetch(`${API_URL}/admin/feedback/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ status }) });
        return safeJson(res, { success: false });
    },
    deleteFeedback: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/feedback/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    }
};
